<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import format from 'date-fns/format';
import addDays from 'date-fns/addDays';
import isSameDay from 'date-fns/isSameDay';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import TasksApi from 'dashboard/api/tasks';
import SearchAPI from 'dashboard/api/search';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { isAdmin } = useAdmin();
const agents = useMapGetter('agents/getAgents');
const tasks = ref([]);
const isLoading = ref(false);
const selectedDate = ref(new Date());
const search = ref('');
const selectedAgentId = ref('');
const isAgentFilterOpen = ref(false);
const rightPanelMode = ref(route.params.id ? 'conversation' : 'planner');
const selectedTask = ref(null);
const showTaskForm = ref(false);
const editingTask = ref(null);
const isSavingTask = ref(false);
const taskPendingDelete = ref(null);
const isDeletingTask = ref(false);
const contactSearch = ref('');
const contactResults = ref([]);
const isSearchingContacts = ref(false);
const taskForm = ref({
  title: '',
  description: '',
  dueAt: format(addDays(new Date(), 0), "yyyy-MM-dd'T'HH:mm"),
  assigneeId: '',
  contactId: '',
});

const isEditingConversationTask = computed(
  () => !!editingTask.value?.conversation?.id
);

const currentConversationId = computed(() => Number(route.params.id));

const visibleDays = computed(() =>
  [-1, 0, 1].map(offset => addDays(new Date(), offset))
);

const dayLabel = day => {
  const today = new Date();
  if (isSameDay(day, today)) return 'Hoje';
  if (isSameDay(day, addDays(today, -1))) return 'Ontem';
  if (isSameDay(day, addDays(today, 1))) return 'Amanhã';
  return format(day, 'dd/MM');
};

const formatTime = dueAt =>
  dueAt ? format(new Date(dueAt), 'HH:mm') : '--:--';

const formatDateTime = dueAt =>
  dueAt ? format(new Date(dueAt), 'dd/MM/yyyy HH:mm') : 'Sem data';

const isOverdue = task => {
  if (!task.due_at || task.status === 'completed') return false;
  return new Date(task.due_at) < new Date();
};

const isSelectedDateToday = computed(() =>
  isSameDay(selectedDate.value, new Date())
);

const taskDueLabel = task => {
  if (isOverdue(task)) {
    return `Atrasada desde ${formatDateTime(task.due_at)}`;
  }
  return formatTime(task.due_at);
};

const selectedTasks = computed(() => {
  const query = search.value.trim().toLowerCase();
  return tasks.value
    .filter(task => {
      const dueAt = task.due_at ? new Date(task.due_at) : null;
      const shouldShowOnSelectedDate =
        dueAt &&
        (isSameDay(dueAt, selectedDate.value) ||
          (isSelectedDateToday.value && isOverdue(task)));

      if (!shouldShowOnSelectedDate) {
        return false;
      }
      if (!query) return true;
      const contactName =
        task.conversation?.contact_name || task.contact?.name || '';
      return `${contactName} ${task.title || ''} ${task.description || ''} ${task.note || ''}`
        .toLowerCase()
        .includes(query);
    })
    .sort((a, b) => {
      const statusOrder =
        Number(a.status === 'completed') - Number(b.status === 'completed');
      if (statusOrder !== 0) return statusOrder;
      const overdueOrder = Number(isOverdue(b)) - Number(isOverdue(a));
      if (overdueOrder !== 0) return overdueOrder;
      return new Date(a.due_at) - new Date(b.due_at);
    });
});

const pendingCount = computed(
  () => tasks.value.filter(task => task.status !== 'completed').length
);
const overdueCount = computed(() => tasks.value.filter(isOverdue).length);

const tasksByHour = computed(() =>
  selectedTasks.value.reduce((acc, task) => {
    const hour = format(new Date(task.due_at), 'HH:00');
    acc[hour] = acc[hour] || [];
    acc[hour].push(task);
    return acc;
  }, {})
);

const plannerHours = computed(() =>
  Array.from(
    { length: 11 },
    (_, index) => `${String(index + 8).padStart(2, '0')}:00`
  )
);

const selectedAgentName = computed(() => {
  if (!selectedAgentId.value) return 'Todos';
  return (
    agents.value.find(
      agent => Number(agent.id) === Number(selectedAgentId.value)
    )?.name || 'Todos'
  );
});

const contactResultsList = computed(() => {
  const payload = contactResults.value?.payload || contactResults.value || [];
  return Array.isArray(payload) ? payload : payload.contacts || [];
});

const fetchTasks = async () => {
  isLoading.value = true;
  try {
    const assigneeId = selectedAgentId.value || undefined;
    const responses = await Promise.all(
      visibleDays.value.map(day =>
        TasksApi.list({
          date: day.toISOString(),
          assignee_id: assigneeId,
          include_overdue: isSameDay(day, new Date()) || undefined,
        })
      )
    );
    const tasksById = new Map();
    responses
      .flatMap(response => response.data.payload || [])
      .forEach(task => {
        tasksById.set(task.id, task);
      });
    tasks.value = Array.from(tasksById.values());
  } catch {
    useAlert('Não foi possível carregar as tarefas');
  } finally {
    isLoading.value = false;
  }
};

const selectAgentFilter = agentId => {
  selectedAgentId.value = agentId;
  isAgentFilterOpen.value = false;
};

const resetTaskForm = () => {
  taskForm.value = {
    title: '',
    description: '',
    dueAt: format(selectedDate.value, "yyyy-MM-dd'T'HH:mm"),
    assigneeId: selectedAgentId.value || '',
    contactId: '',
  };
  contactSearch.value = '';
  contactResults.value = [];
};

const closeTaskForm = () => {
  showTaskForm.value = false;
  editingTask.value = null;
  resetTaskForm();
};

const openEditTask = task => {
  editingTask.value = task;
  taskForm.value = {
    title: task.title || '',
    description: task.description || task.note || '',
    dueAt: task.due_at
      ? format(new Date(task.due_at), "yyyy-MM-dd'T'HH:mm")
      : format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    assigneeId: task.assignee?.id || '',
    contactId: task.contact?.id || '',
  };
  contactSearch.value =
    task.contact?.name || task.conversation?.contact_name || '';
  contactResults.value = [];
  showTaskForm.value = true;
};

const openTaskDetails = task => {
  selectedTask.value = task;
  rightPanelMode.value = 'task';
  if (currentConversationId.value) {
    router.push({ name: 'inbox_view' });
  }
};

const openLinkedConversation = task => {
  if (!task.conversation?.id) return;

  selectedTask.value = task;
  rightPanelMode.value = 'conversation';
  router.push({
    name: 'inbox_view_conversation',
    params: { type: 'conversation', id: task.conversation.id },
  });
};

const closeRightPanel = () => {
  rightPanelMode.value = 'planner';
  selectedTask.value = null;
  if (currentConversationId.value) {
    router.push({ name: 'inbox_view' });
  }
};

const toggleTaskStatus = async task => {
  try {
    if (task.status === 'completed') {
      await TasksApi.reopen(task.id);
    } else {
      await TasksApi.complete(task.id);
    }
    await fetchTasks();
    if (selectedTask.value?.id === task.id) {
      selectedTask.value =
        tasks.value.find(item => item.id === task.id) || task;
    }
  } catch {
    useAlert('Não foi possível atualizar a tarefa');
  }
};

const searchContacts = async () => {
  const query = contactSearch.value.trim();
  if (query.length < 2) {
    contactResults.value = [];
    return;
  }

  isSearchingContacts.value = true;
  try {
    const response = await SearchAPI.contacts({ q: query, page: 1 });
    contactResults.value = response.data;
  } catch {
    useAlert('Não foi possível buscar contatos');
  } finally {
    isSearchingContacts.value = false;
  }
};

const selectContact = contact => {
  taskForm.value.contactId = contact.id;
  contactSearch.value =
    contact.name || contact.email || contact.phone_number || '';
  contactResults.value = [];
};

const saveTask = async () => {
  if (!taskForm.value.title.trim() && !isEditingConversationTask.value) {
    useAlert('Informe o título da tarefa');
    return;
  }

  isSavingTask.value = true;
  const payload = {
    title: taskForm.value.title.trim() || undefined,
    description: taskForm.value.description.trim() || undefined,
    due_at: new Date(taskForm.value.dueAt).toISOString(),
    task_type: 'task',
    assignee_id: taskForm.value.assigneeId || undefined,
    contact_id: taskForm.value.contactId || undefined,
  };

  try {
    if (editingTask.value) {
      await TasksApi.update(editingTask.value.id, payload);
    } else {
      await TasksApi.create(payload);
    }
    const editedTaskId = editingTask.value?.id;
    closeTaskForm();
    await fetchTasks();
    if (selectedTask.value?.id === editedTaskId) {
      selectedTask.value =
        tasks.value.find(item => item.id === editedTaskId) ||
        selectedTask.value;
    }
  } catch {
    useAlert(
      editingTask.value
        ? 'Não foi possível atualizar a tarefa'
        : 'Não foi possível criar a tarefa'
    );
  } finally {
    isSavingTask.value = false;
  }
};

const confirmDeleteTask = task => {
  taskPendingDelete.value = task;
};

const cancelDeleteTask = () => {
  taskPendingDelete.value = null;
};

const deleteTask = async () => {
  if (!taskPendingDelete.value) return;
  isDeletingTask.value = true;

  try {
    await TasksApi.delete(taskPendingDelete.value.id);
    if (selectedTask.value?.id === taskPendingDelete.value.id) {
      closeRightPanel();
    }
    taskPendingDelete.value = null;
    await fetchTasks();
  } catch {
    useAlert('Não foi possível excluir a tarefa');
  } finally {
    isDeletingTask.value = false;
  }
};

const taskTitle = task =>
  task.title ||
  task.conversation?.contact_name ||
  task.contact?.name ||
  'Tarefa';

const taskDescription = task =>
  task.description || task.note || 'Sem descrição';

const taskContactName = task =>
  task.conversation?.contact_name ||
  task.contact?.name ||
  'Sem contato vinculado';

watch(selectedAgentId, fetchTasks);

watch(
  () => route.params.id,
  id => {
    if (id) rightPanelMode.value = 'conversation';
  }
);

onMounted(async () => {
  await store.dispatch('agents/get');
  await fetchTasks();
});
</script>

<template>
  <section
    class="flex h-full w-full bg-[#f7f8fb] text-n-slate-12 dark:bg-n-background"
  >
    <aside
      class="flex h-full w-full flex-col border-r border-n-weak bg-white/85 backdrop-blur-xl dark:bg-n-solid-2/85 lg:min-w-[390px] lg:max-w-[390px]"
      :class="currentConversationId ? 'hidden xl:flex' : 'flex'"
    >
      <header class="border-b border-n-weak px-6 py-5">
        <div class="flex items-center justify-between gap-4">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-3">
              <h1 class="text-xl font-semibold tracking-[-0.02em]">Tarefas</h1>
              <div class="relative">
                <button
                  type="button"
                  class="inline-flex h-10 min-w-[132px] max-w-[190px] items-center gap-2 rounded-2xl border border-n-brand/25 bg-white px-3 text-sm font-semibold text-n-slate-12 shadow-sm transition-colors hover:bg-n-alpha-1 dark:bg-n-solid-3"
                  @click="isAgentFilterOpen = !isAgentFilterOpen"
                >
                  <span
                    class="i-lucide-user-round size-4 shrink-0 text-n-brand"
                  />
                  <span class="min-w-0 flex-1 truncate leading-none">
                    {{ selectedAgentName }}
                  </span>
                  <span
                    class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-9"
                  />
                </button>
                <div
                  v-if="isAgentFilterOpen"
                  class="absolute left-0 top-12 z-20 w-56 overflow-hidden rounded-2xl border border-n-weak bg-white p-1 shadow-xl shadow-slate-900/10 dark:bg-n-solid-2"
                >
                  <button
                    type="button"
                    class="flex h-10 w-full items-center justify-between rounded-xl px-3 text-left text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1"
                    @click="selectAgentFilter('')"
                  >
                    <span>Todos</span>
                    <span
                      v-if="!selectedAgentId"
                      class="i-lucide-check size-4 text-n-brand"
                    />
                  </button>
                  <button
                    v-for="agent in agents"
                    :key="agent.id"
                    type="button"
                    class="flex h-10 w-full items-center justify-between rounded-xl px-3 text-left text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1"
                    @click="selectAgentFilter(agent.id)"
                  >
                    <span class="min-w-0 truncate">{{ agent.name }}</span>
                    <span
                      v-if="Number(selectedAgentId) === Number(agent.id)"
                      class="i-lucide-check size-4 shrink-0 text-n-brand"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="grid size-10 place-content-center rounded-2xl bg-n-brand text-white shadow-sm transition-transform hover:scale-105"
            @click="showTaskForm = true"
          >
            <span class="i-lucide-plus size-5" />
          </button>
        </div>

        <div class="mt-5 grid grid-cols-2 gap-2">
          <div class="rounded-2xl bg-n-alpha-1 px-4 py-3">
            <p class="text-xs text-n-slate-10">Pendentes</p>
            <p class="mt-1 text-xl font-semibold">{{ pendingCount }}</p>
          </div>
          <div
            class="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-red-700 dark:border-red-500/40 dark:bg-n-solid-3 dark:text-red-300"
          >
            <p class="text-xs text-red-500 dark:text-red-300/80">Atrasadas</p>
            <p class="mt-1 text-xl font-semibold">{{ overdueCount }}</p>
          </div>
        </div>

        <label
          class="mt-4 flex h-11 items-center gap-2 rounded-2xl border border-n-weak bg-white px-3 text-sm text-n-slate-11 shadow-sm dark:bg-n-solid-3"
        >
          <span class="i-lucide-search size-4" />
          <input
            v-model="search"
            class="min-w-0 flex-1 bg-transparent outline-none placeholder:text-n-slate-9"
            placeholder="Buscar tarefas"
          />
        </label>

        <div class="mt-4 flex gap-2">
          <button
            v-for="day in visibleDays"
            :key="day.toISOString()"
            type="button"
            class="rounded-full px-4 py-2 text-sm font-medium transition-colors"
            :class="
              isSameDay(day, selectedDate)
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="selectedDate = day"
          >
            {{ dayLabel(day) }}
          </button>
        </div>
      </header>

      <div class="flex-1 overflow-y-auto px-3 py-3">
        <div v-if="isLoading" class="flex h-40 items-center justify-center">
          <Spinner class="text-n-brand" />
        </div>

        <div v-else-if="selectedTasks.length" class="space-y-2">
          <button
            v-for="task in selectedTasks"
            :key="task.id"
            type="button"
            class="group w-full rounded-2xl border px-4 py-3 text-left shadow-sm transition-all hover:border-n-brand/20 hover:shadow-md"
            :class="[
              task.status === 'completed'
                ? 'border-transparent bg-n-alpha-1 opacity-75'
                : isOverdue(task)
                  ? 'border-red-300 bg-red-50/70 dark:border-red-500/60 dark:bg-n-solid-3 dark:shadow-[inset_3px_0_0_rgba(239,68,68,0.75)]'
                  : 'border-[#D6E9FF] bg-[#EAF4FF] dark:border-n-weak dark:bg-n-alpha-2',
              selectedTask?.id === task.id &&
              task.status !== 'completed' &&
              !isOverdue(task)
                ? 'border-n-brand/30 bg-blue-50 dark:bg-n-alpha-3'
                : '',
            ]"
            @click="openTaskDetails(task)"
          >
            <div class="flex items-start gap-3">
              <button
                type="button"
                class="mt-1 grid size-6 shrink-0 place-content-center rounded-full border-2 transition-colors"
                :class="
                  task.status === 'completed'
                    ? 'border-slate-500 bg-white text-slate-600 dark:bg-n-solid-2 dark:text-n-slate-9'
                    : 'border-slate-500 bg-white text-slate-500 group-hover:border-n-brand group-hover:text-n-brand dark:bg-n-solid-2'
                "
                @click.stop="toggleTaskStatus(task)"
              >
                <span
                  v-if="task.status === 'completed'"
                  class="i-lucide-check size-3.5"
                />
              </button>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p
                    class="truncate text-sm font-semibold"
                    :class="
                      task.status === 'completed'
                        ? 'text-n-slate-10 line-through'
                        : ''
                    "
                  >
                    {{ taskTitle(task) }}
                  </p>
                </div>
                <p
                  class="mt-1 line-clamp-2 text-sm text-n-slate-11"
                  :class="
                    task.status === 'completed'
                      ? 'text-n-slate-9 line-through'
                      : ''
                  "
                >
                  {{ taskDescription(task) }}
                </p>
                <p class="mt-2 truncate text-xs text-n-slate-9">
                  {{ taskContactName(task) }}
                </p>
                <p
                  class="mt-1 truncate text-xs font-semibold"
                  :class="isOverdue(task) ? 'text-red-600' : 'text-n-blue-text'"
                >
                  {{ taskDueLabel(task) }}
                </p>
              </div>
            </div>
          </button>
        </div>

        <div
          v-else
          class="rounded-3xl border border-dashed border-n-weak bg-white px-6 py-10 text-center dark:bg-n-solid-2"
        >
          <span
            class="i-lucide-calendar-check mx-auto block size-8 text-n-slate-9"
          />
          <p class="mt-3 text-sm font-medium">Nenhuma tarefa nesse dia</p>
          <p class="mt-1 text-sm text-n-slate-10">
            Crie follow-ups ou agendamentos pelo app ou pela conversa.
          </p>
        </div>
      </div>
    </aside>

    <main class="hidden min-w-0 flex-1 flex-col xl:flex">
      <template v-if="rightPanelMode === 'planner'">
        <header
          class="flex h-[73px] items-center justify-between border-b border-n-weak bg-white/80 px-8 backdrop-blur-xl dark:bg-n-solid-2/80"
        >
          <div>
            <h2 class="text-lg font-semibold">Agenda do dia</h2>
            <p class="text-sm text-n-slate-10">
              {{ format(selectedDate, 'dd/MM/yyyy') }}
            </p>
          </div>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-2xl bg-n-alpha-1 px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
            @click="fetchTasks"
          >
            <span class="i-lucide-refresh-cw size-4" />
            Atualizar
          </button>
        </header>

        <div class="flex-1 overflow-y-auto p-8">
          <div
            class="mx-auto max-w-5xl rounded-[28px] border border-n-weak bg-white shadow-sm dark:bg-n-solid-2"
          >
            <div
              v-for="hour in plannerHours"
              :key="hour"
              class="grid min-h-[78px] grid-cols-[88px_1fr] border-b border-n-weak last:border-b-0"
            >
              <div
                class="border-r border-n-weak px-4 py-4 text-sm font-medium text-n-slate-9"
              >
                {{ hour }}
              </div>
              <div class="flex flex-wrap gap-2 px-4 py-3">
                <button
                  v-for="task in tasksByHour[hour] || []"
                  :key="task.id"
                  type="button"
                  class="rounded-2xl border px-4 py-2 text-left text-sm shadow-sm"
                  :class="
                    isOverdue(task)
                      ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/50 dark:bg-n-solid-3 dark:text-red-300 dark:shadow-[inset_3px_0_0_rgba(239,68,68,0.75)]'
                      : 'border-blue-100 bg-blue-50 text-blue-800 dark:border-n-weak dark:bg-n-alpha-2 dark:text-n-brand'
                  "
                  @click="openTaskDetails(task)"
                >
                  <span class="font-semibold">{{ taskTitle(task) }}</span>
                  <span class="ml-2 text-xs opacity-70">{{
                    taskDescription(task)
                  }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="rightPanelMode === 'task' && selectedTask">
        <header
          class="flex h-[73px] items-center justify-between border-b border-n-weak bg-white/80 px-8 backdrop-blur-xl dark:bg-n-solid-2/80"
        >
          <div class="min-w-0">
            <h2 class="truncate text-lg font-semibold">
              {{ taskTitle(selectedTask) }}
            </h2>
            <p class="text-sm text-n-slate-10">
              {{ formatDateTime(selectedTask.due_at) }}
            </p>
          </div>
          <button
            type="button"
            class="grid size-10 place-content-center rounded-full bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2"
            @click="closeRightPanel"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </header>

        <div class="flex-1 overflow-y-auto p-8">
          <div
            class="mx-auto max-w-2xl rounded-[28px] border border-n-weak bg-white p-6 shadow-sm dark:bg-n-solid-2"
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <p
                  class="text-xs font-medium uppercase tracking-[0.18em] text-n-slate-9"
                >
                  Detalhes da tarefa
                </p>
                <h3 class="mt-2 text-2xl font-semibold tracking-[-0.02em]">
                  {{ taskTitle(selectedTask) }}
                </h3>
              </div>
              <span
                class="rounded-full px-3 py-1 text-xs font-semibold"
                :class="
                  selectedTask.status === 'completed'
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                    : 'bg-blue-50 text-blue-700 dark:bg-n-alpha-2 dark:text-n-brand'
                "
              >
                {{
                  selectedTask.status === 'completed' ? 'Concluída' : 'Pendente'
                }}
              </span>
            </div>

            <p
              class="mt-5 whitespace-pre-wrap text-sm leading-6 text-n-slate-11"
            >
              {{ taskDescription(selectedTask) }}
            </p>

            <div class="mt-6 grid gap-3 text-sm">
              <div
                class="flex items-center justify-between rounded-2xl bg-n-alpha-1 px-4 py-3"
              >
                <span class="text-n-slate-10">Contato</span>
                <span class="font-medium">{{
                  taskContactName(selectedTask)
                }}</span>
              </div>
              <div
                class="flex items-center justify-between rounded-2xl bg-n-alpha-1 px-4 py-3"
              >
                <span class="text-n-slate-10">Agente</span>
                <span class="font-medium">{{
                  selectedTask.assignee?.name || 'Todos'
                }}</span>
              </div>
              <div
                class="flex items-center justify-between rounded-2xl bg-n-alpha-1 px-4 py-3"
              >
                <span class="text-n-slate-10">Criado por</span>
                <span class="font-medium">{{
                  selectedTask.created_by?.name || 'Não informado'
                }}</span>
              </div>
            </div>

            <div class="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                class="inline-flex h-11 items-center gap-2 rounded-2xl bg-n-brand px-4 text-sm font-semibold text-white"
                @click="toggleTaskStatus(selectedTask)"
              >
                <span class="i-lucide-check-circle size-4" />
                {{
                  selectedTask.status === 'completed' ? 'Reabrir' : 'Concluir'
                }}
              </button>
              <button
                v-if="selectedTask.conversation?.id"
                type="button"
                class="inline-flex h-11 items-center gap-2 rounded-2xl border border-n-weak bg-white px-4 text-sm font-semibold text-n-slate-12 dark:bg-n-solid-3"
                @click="openLinkedConversation(selectedTask)"
              >
                <span class="i-lucide-message-circle size-4" />
                Abrir conversa
              </button>
              <button
                v-if="isAdmin"
                type="button"
                class="inline-flex h-11 items-center gap-2 rounded-2xl border border-n-weak bg-white px-4 text-sm font-semibold text-n-slate-12 dark:bg-n-solid-3"
                @click="openEditTask(selectedTask)"
              >
                <span class="i-lucide-pencil size-4" />
                Editar
              </button>
              <button
                v-if="isAdmin"
                type="button"
                class="inline-flex h-11 items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300"
                @click="confirmDeleteTask(selectedTask)"
              >
                <span class="i-lucide-trash-2 size-4" />
                Excluir
              </button>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <header
          class="flex h-[73px] items-center justify-between border-b border-n-weak bg-white/80 px-8 backdrop-blur-xl dark:bg-n-solid-2/80"
        >
          <div>
            <h2 class="text-lg font-semibold">Conversa vinculada</h2>
            <p class="text-sm text-n-slate-10">
              Atendimento relacionado à tarefa
            </p>
          </div>
          <button
            type="button"
            class="grid size-10 place-content-center rounded-full bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2"
            @click="closeRightPanel"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </header>
        <div class="min-h-0 flex-1">
          <router-view />
        </div>
      </template>
    </main>

    <div
      v-if="showTaskForm"
      class="fixed inset-0 z-50 grid place-items-center bg-black/20 p-4 backdrop-blur-sm"
      @click.self="closeTaskForm"
    >
      <form
        class="w-full max-w-md rounded-[28px] border border-white/70 bg-white p-5 shadow-2xl shadow-slate-900/10 dark:border-n-weak dark:bg-n-solid-2"
        @submit.prevent="saveTask"
      >
        <div class="flex items-center justify-between gap-3">
          <h3 class="text-lg font-semibold">
            {{ editingTask ? 'Editar tarefa' : 'Nova tarefa' }}
          </h3>
          <button
            type="button"
            class="grid size-9 place-content-center rounded-full bg-n-alpha-1 text-n-slate-11"
            @click="closeTaskForm"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
        <label class="mt-5 block text-sm font-medium text-n-slate-11">
          Título
          <input
            v-model="taskForm.title"
            class="mt-2 h-11 w-full rounded-2xl border border-n-weak bg-[#f7f8fb] px-3 outline-none focus:border-n-brand dark:bg-n-solid-3"
            placeholder="Ex: Retornar orçamento"
          />
        </label>
        <label class="mt-4 block text-sm font-medium text-n-slate-11">
          Vincular agente
          <select
            v-model="taskForm.assigneeId"
            class="mt-2 h-11 w-full rounded-2xl border border-n-weak bg-[#f7f8fb] px-3 outline-none focus:border-n-brand dark:bg-n-solid-3"
          >
            <option value="">Todos</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </label>
        <label class="mt-4 block text-sm font-medium text-n-slate-11">
          Selecionar contato
          <div
            class="mt-2 flex h-11 items-center gap-2 rounded-2xl border border-n-weak bg-[#f7f8fb] px-3 focus-within:border-n-brand dark:bg-n-solid-3"
          >
            <input
              v-model="contactSearch"
              class="min-w-0 flex-1 bg-transparent outline-none"
              placeholder="Buscar por nome, telefone ou e-mail"
              @input="searchContacts"
            />
            <Spinner v-if="isSearchingContacts" class="size-4 text-n-brand" />
          </div>
        </label>
        <div
          v-if="contactResultsList.length"
          class="mt-2 max-h-36 overflow-y-auto rounded-2xl border border-n-weak bg-white p-1 shadow-sm dark:bg-n-solid-2"
        >
          <button
            v-for="contact in contactResultsList"
            :key="contact.id"
            type="button"
            class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm hover:bg-n-alpha-1"
            @click="selectContact(contact)"
          >
            <span class="font-medium">{{
              contact.name || contact.email || contact.phone_number
            }}</span>
            <span class="text-xs text-n-slate-9">Selecionar</span>
          </button>
        </div>
        <label class="mt-4 block text-sm font-medium text-n-slate-11">
          Data e hora
          <input
            v-model="taskForm.dueAt"
            type="datetime-local"
            class="mt-2 h-11 w-full rounded-2xl border border-n-weak bg-[#f7f8fb] px-3 outline-none focus:border-n-brand dark:bg-n-solid-3"
          />
        </label>
        <label class="mt-4 block text-sm font-medium text-n-slate-11">
          Descrição
          <textarea
            v-model="taskForm.description"
            class="mt-2 min-h-24 w-full resize-none rounded-2xl border border-n-weak bg-[#f7f8fb] px-3 py-3 outline-none focus:border-n-brand dark:bg-n-solid-3"
            placeholder="Detalhes opcionais"
          />
        </label>
        <button
          type="submit"
          class="mt-5 h-11 w-full rounded-2xl bg-n-brand text-sm font-semibold text-white disabled:opacity-50"
          :disabled="isSavingTask"
        >
          {{ editingTask ? 'Salvar alterações' : 'Criar tarefa' }}
        </button>
      </form>
    </div>

    <div
      v-if="taskPendingDelete"
      class="fixed inset-0 z-50 grid place-items-center bg-black/20 p-4 backdrop-blur-sm"
      @click.self="cancelDeleteTask"
    >
      <div
        class="w-full max-w-sm rounded-[28px] border border-white/70 bg-white p-5 shadow-2xl shadow-slate-900/10 dark:border-n-weak dark:bg-n-solid-2"
      >
        <h3 class="text-lg font-semibold">Excluir tarefa</h3>
        <p class="mt-2 text-sm text-n-slate-11">
          Tem certeza que deseja excluir esta tarefa? Essa ação não pode ser
          desfeita.
        </p>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="h-10 rounded-2xl bg-n-alpha-1 px-4 text-sm font-medium text-n-slate-12"
            @click="cancelDeleteTask"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="h-10 rounded-2xl bg-red-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
            :disabled="isDeletingTask"
            @click="deleteTask"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
